/**
 * Typed cookie helpers for tracker preferences and flight selection.
 *
 * omen_tracker_prefs     — 365 days (renewed on every write)
 * omen_tracker_selection — 5 days, renewed on every write
 */

import Cookies from 'js-cookie';

export interface TrackerPrefs {
  flightColors: string[];
  defaultShowNormal: boolean;
  defaultShowInstant: boolean;
  /** 'street' = vector tiles, 'satellite' = ArcGIS raster + labels (formerly 'satellite_labels'). */
  defaultMapStyle: 'street' | 'satellite';
  globalShowPolygon: boolean;
  globalShowHeatmap: boolean;
  pollingIntervalSec: number;
  flightDateFilterMonths: number;
  /** html font-size in px (12–20). Applied via document.documentElement.style.fontSize. */
  fontSize: number;
  /** Side panel width in px when expanded (300–450). */
  panelWidth: number;
}

export interface PerFlightSelection {
  showNormal: boolean;
  showInstant: boolean;
  showTeams: boolean;
  isVisible: boolean;
}

export interface TrackerSelection {
  selectedFlightIds: number[];
  perFlight: Record<number, PerFlightSelection>;
}

export const DEFAULT_PREFS: TrackerPrefs = {
  flightColors: ['#FF3B30', '#007AFF', '#FF9500', '#AF52DE', '#00C7BE'],
  defaultShowNormal: true,
  defaultShowInstant: false,
  defaultMapStyle: 'street',
  globalShowPolygon: true,
  globalShowHeatmap: true,
  pollingIntervalSec: 60,
  flightDateFilterMonths: 3,
  fontSize: 14,
  panelWidth: 300,
};

/**
 * Persists tracker preferences. No expiry — cookie survives browser restarts.
 * @param prefs - full TrackerPrefs object
 */
export const savePrefs = (prefs: TrackerPrefs): void => {
  Cookies.set('omen_tracker_prefs', JSON.stringify(prefs), { expires: 365 });
};

/**
 * Persists flight selection with a rolling 7-day expiry.
 * Call on every selection change to renew the window.
 * @param sel - full TrackerSelection object
 */
export const saveSelection = (sel: TrackerSelection): void => {
  Cookies.set('omen_tracker_selection', JSON.stringify(sel), { expires: 5 });
};

/**
 * Reads persisted tracker preferences.
 * Applies migrations for stale cookie values so the app never crashes on
 * unknown enum values:
 *   - old 'satellite' (raw ArcGIS raster, removed) → new 'satellite' (hybrid)
 *   - old 'satellite_labels' (former hybrid key) → new 'satellite' (same content)
 * @returns TrackerPrefs or null if cookie is absent or malformed
 */
export const loadPrefs = (): TrackerPrefs | null => {
  try {
    const raw = Cookies.get('omen_tracker_prefs');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TrackerPrefs;
    // Migrate map style: any value not in the current two-entry set → 'satellite'
    if (parsed.defaultMapStyle !== 'street' && parsed.defaultMapStyle !== 'satellite') {
      parsed.defaultMapStyle = 'satellite';
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Reads persisted flight selection.
 * @returns TrackerSelection or null if cookie is absent or malformed
 */
export const loadSelection = (): TrackerSelection | null => {
  try {
    const raw = Cookies.get('omen_tracker_selection');
    if (!raw) return null;
    return JSON.parse(raw) as TrackerSelection;
  } catch {
    return null;
  }
};
